import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Type,
  Music,
  Trash2,
  X,
  Plus,
  GripVertical,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import {
  parseChordProToCanvas,
  canvasToChordPro,
  getUsedChords,
  type CanvasDoc,
  type CanvasLine,
  type CanvasWord,
} from "../../lib/canvasFormat";
import { smartPaste, type SmartPasteResult } from "../../lib/smartPaste";
import { SmartPasteModal } from "./SmartPasteModal";

// ─── Paleta de acordes comunes ───
const ROOT_NOTES = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];
const QUALITIES: Array<{ label: string; value: string }> = [
  { label: "maj", value: "" },
  { label: "m", value: "m" },
  { label: "7", value: "7" },
  { label: "maj7", value: "maj7" },
  { label: "m7", value: "m7" },
  { label: "sus4", value: "sus4" },
  { label: "sus2", value: "sus2" },
  { label: "dim", value: "dim" },
  { label: "aug", value: "aug" },
  { label: "add9", value: "add9" },
  { label: "6", value: "6" },
  { label: "m6", value: "m6" },
  { label: "9", value: "9" },
  { label: "m9", value: "m9" },
  { label: "7sus4", value: "7sus4" },
  { label: "mMaj7", value: "mMaj7" },
  { label: "dim7", value: "dim7" },
  { label: "aug7", value: "aug7" },
];

interface Props {
  raw: string;
  onChange: (raw: string) => void;
}

type EditMode = "write" | "chords";

export const CanvasEditor: React.FC<Props> = ({ raw, onChange }) => {
  const [doc, setDoc] = useState<CanvasDoc>(() => parseChordProToCanvas(raw));
  const [mode, setMode] = useState<EditMode>("write");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hoveredChord, setHoveredChord] = useState<string | null>(null);
  const [chordPickerOpen, setChordPickerOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ x: 0, y: 0 });
  const [customChord, setCustomChord] = useState("");
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [writeBuffer, setWriteBuffer] = useState("");
  const [pasteResult, setPasteResult] = useState<SmartPasteResult | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Sincronizar cuando raw cambia externamente
  useEffect(() => {
    const parsed = parseChordProToCanvas(raw);
    // Solo actualizar si el contenido es sustancialmente diferente
    // para no perder selección durante edición propia
    const currentRaw = canvasToChordPro(doc);
    if (currentRaw !== raw) {
      setDoc(parsed);
      setSelectedIds(new Set());
    }
  }, [raw]);

  // Emitir cambio
  const emit = useCallback(
    (newDoc: CanvasDoc) => {
      setDoc(newDoc);
      onChange(canvasToChordPro(newDoc));
    },
    [onChange]
  );

  const usedChords = useMemo(() => getUsedChords(doc), [doc]);

  // ─── Modo Escritura ───
  const handleWriteInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;
      setWriteBuffer(text);

      // Parsear a CanvasDoc
      const newDoc = parseChordProToCanvas(text);
      // Solo conservar metadatos existentes si el texto no trae los suyos
      if (!newDoc.title) newDoc.title = doc.title;
      if (!newDoc.artist) newDoc.artist = doc.artist;
      if (!newDoc.key) newDoc.key = doc.key;
      if (!newDoc.tempo) newDoc.tempo = doc.tempo;
      if (!newDoc.capo) newDoc.capo = doc.capo;
      emit(newDoc);
    },
    [doc, emit]
  );

  // ─── Pegado inteligente ───
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const text = e.clipboardData.getData("text/plain");
      if (!text || text.length < 3) return;

      e.preventDefault();
      // Forzar que el textarea no quede con texto crudo si el preventDefault falla
      const el = e.currentTarget;
      requestAnimationFrame(() => {
        if (el.value !== (writeBuffer || raw)) {
          el.value = writeBuffer || raw;
        }
      });
      const result = smartPaste(text);
      setPasteResult(result);
    },
    [writeBuffer, raw]
  );

  const handleSmartPasteManual = useCallback(() => {
    navigator.clipboard.readText().then((text) => {
      if (text) {
        const result = smartPaste(text);
        setPasteResult(result);
      }
    });
  }, []);

  const applyPastedText = useCallback(
    (text: string) => {
      setWriteBuffer(text);
      const newDoc = parseChordProToCanvas(text);
      // Preservar metadatos existentes SOLO si el texto procesado no trae los suyos
      if (!newDoc.title) newDoc.title = doc.title;
      if (!newDoc.artist) newDoc.artist = doc.artist;
      if (!newDoc.key) newDoc.key = doc.key;
      if (!newDoc.tempo) newDoc.tempo = doc.tempo;
      if (!newDoc.capo) newDoc.capo = doc.capo;
      emit(newDoc);
      setPasteResult(null);
    },
    [doc, emit]
  );

  const handleUseProcessed = useCallback(() => {
    if (pasteResult) applyPastedText(pasteResult.processed);
  }, [pasteResult, applyPastedText]);

  const handleUseOriginal = useCallback(() => {
    if (pasteResult) applyPastedText(pasteResult.original);
  }, [pasteResult, applyPastedText]);

  // ─── Selección de palabras ───
  const toggleWordSelection = useCallback(
    (wordId: string, event: React.MouseEvent) => {
      if (mode !== "chords") return;

      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (event.ctrlKey || event.metaKey) {
          if (next.has(wordId)) next.delete(wordId);
          else next.add(wordId);
        } else {
          next.clear();
          next.add(wordId);
        }
        return next;
      });
    },
    [mode]
  );

  // ─── Abrir picker de acordes ───
  const openChordPicker = useCallback(
    (wordId: string, event: React.MouseEvent) => {
      if (mode !== "chords") return;

      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setPickerPosition({ x: rect.left + rect.width / 2, y: rect.top - 8 });
      setChordPickerOpen(true);

      if (!selectedIds.has(wordId)) {
        setSelectedIds(new Set([wordId]));
      }
    },
    [mode, selectedIds]
  );

  // ─── Aplicar acorde a selección ───
  const applyChord = useCallback(
    (chord: string) => {
      if (selectedIds.size === 0) return;

      const newDoc: CanvasDoc = {
        ...doc,
        lines: doc.lines.map((line) => ({
          ...line,
          words: line.words.map((word) =>
            selectedIds.has(word.id) ? { ...word, chord } : word
          ),
        })),
      };
      emit(newDoc);
      setChordPickerOpen(false);
      setCustomChord("");
    },
    [doc, selectedIds, emit]
  );

  // ─── Eliminar acorde de selección ───
  const removeChordFromSelection = useCallback(() => {
    if (selectedIds.size === 0) return;
    const newDoc: CanvasDoc = {
      ...doc,
      lines: doc.lines.map((line) => ({
        ...line,
        words: line.words.map((word) =>
          selectedIds.has(word.id) ? { ...word, chord: null } : word
        ),
      })),
    };
    emit(newDoc);
    setChordPickerOpen(false);
  }, [doc, selectedIds, emit]);

  // ─── Eliminar palabras seleccionadas ───
  const deleteSelectedWords = useCallback(() => {
    if (selectedIds.size === 0) return;
    const newDoc: CanvasDoc = {
      ...doc,
      lines: doc.lines.map((line) => ({
        ...line,
        words: line.words.filter((word) => !selectedIds.has(word.id)),
      })).filter((line) => line.type !== "line" || line.words.length > 0),
    };
    emit(newDoc);
    setSelectedIds(new Set());
  }, [doc, selectedIds, emit]);

  // ─── Agregar línea vacía ───
  const addLine = useCallback(
    (afterIndex: number) => {
      const lines = [...doc.lines];
      lines.splice(afterIndex + 1, 0, {
        id: `line_${Date.now()}`,
        type: "line",
        words: [{ id: `word_${Date.now()}`, text: "", chord: null }],
      });
      emit({ ...doc, lines });
    },
    [doc, emit]
  );

  // ─── Agregar sección ───
  const addSection = useCallback(
    (name: string, afterIndex: number) => {
      const lines = [...doc.lines];
      lines.splice(afterIndex + 1, 0, {
        id: `section_${Date.now()}`,
        type: "section",
        name,
        words: [],
      });
      emit({ ...doc, lines });
    },
    [doc, emit]
  );

  // ─── Click fuera para deseleccionar ───
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (canvasRef.current && !canvasRef.current.contains(e.target as Node)) {
        setSelectedIds(new Set());
        setChordPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─── Key bindings ───
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.size > 0 && mode === "chords") {
          e.preventDefault();
          deleteSelectedWords();
        }
      }
      if (e.key === "Escape") {
        setSelectedIds(new Set());
        setChordPickerOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedIds, mode, deleteSelectedWords]);

  // ─── Focus input en modo escritura ───
  useEffect(() => {
    if (mode === "write" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  return (
    <div className="h-full flex flex-col" ref={canvasRef}>
      {/* ─── Toolbar ─── */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E0D4] bg-[#FAF8F5]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setMode("write"); setSelectedIds(new Set()); setWriteBuffer(""); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              mode === "write"
                ? "bg-[#7B2335] text-white"
                : "text-[#5A524A] hover:bg-[#F0EAE0]"
            }`}
          >
            <Type className="w-3.5 h-3.5" />
            Escribe la letra
          </button>
          <button
            onClick={() => setMode("chords")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              mode === "chords"
                ? "bg-[#7B2335] text-white"
                : "text-[#5A524A] hover:bg-[#F0EAE0]"
            }`}
          >
            <Music className="w-3.5 h-3.5" />
            Revisa los acordes
          </button>
        </div>

        <div className="flex items-center gap-2">
          {mode === "write" && (
            <button
              onClick={handleSmartPasteManual}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-medium bg-[#FDE68A] text-[#92400E] hover:bg-[#FCD34D] transition-colors"
              title="Pegar inteligente (Ctrl+V también funciona)"
            >
              <Sparkles className="w-3 h-3" />
              Pegar inteligente
            </button>
          )}
          {mode === "chords" && selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1.5 text-[10px] text-[#7B2335] font-semibold bg-[#7B2335]/10 px-2 py-1 rounded-lg"
            >
              {selectedIds.size} seleccionada{selectedIds.size > 1 ? "s" : ""}
              <button onClick={deleteSelectedWords} className="hover:text-red-600 ml-1">
                <Trash2 className="w-3 h-3" />
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="hover:text-[#5A524A] ml-0.5">
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          )}
          {usedChords.length > 0 && (
            <div className="hidden md:flex items-center gap-1 text-[10px] text-[#8A8078]">
              Usados:
              {usedChords.map((c) => (
                <button
                  key={c}
                  onClick={() => applyChord(c)}
                  className="px-1.5 py-0.5 rounded bg-[#F0EAE0] hover:bg-[#7B2335] hover:text-white transition-colors font-semibold"
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Área de edición ─── */}
      <div className="flex-1 overflow-y-auto relative">
        {mode === "write" ? (
          <div className="h-full p-6">
            <textarea
              ref={inputRef}
              value={writeBuffer || raw}
              onChange={handleWriteInput}
              onPaste={handlePaste}
              className="w-full h-full p-4 rounded-xl border border-[#E8E0D4] bg-white font-mono text-sm text-[#1F1F1F] resize-none focus:outline-none focus:ring-2 focus:ring-[#7B2335]/20 leading-relaxed"
              spellCheck={false}
              placeholder={`Escribe la letra de tu canción...\n\nPuedes usar formato ChordPro: [C]Primera [G]línea...\n\nO simplemente escribir la letra y luego pasar al modo Revisa los acordes.`}
            />
          </div>
        ) : (
          <div className="min-h-full p-6 pb-32 space-y-1">
            {/* Metadata */}
            {(doc.title || doc.artist) && (
              <div className="text-center pb-4 mb-4 border-b border-[#E8E0D4]">
                {doc.title && (
                  <h1 className="text-xl font-bold text-[#2C2825]">{doc.title}</h1>
                )}
                {doc.artist && (
                  <p className="text-sm text-[#8A8078] mt-1">{doc.artist}</p>
                )}
              </div>
            )}

            {/* Líneas */}
            {doc.lines.map((line, lineIdx) => (
              <CanvasLineRow
                key={line.id}
                line={line}
                lineIndex={lineIdx}
                isActive={activeLineId === line.id}
                selectedIds={selectedIds}
                onWordClick={toggleWordSelection}
                onWordDoubleClick={openChordPicker}
                onLineClick={() => setActiveLineId(line.id)}
                onAddLine={() => addLine(lineIdx)}
                onAddSection={(name) => addSection(name, lineIdx)}
              />
            ))}

            {/* Agregar línea al final */}
            <button
              onClick={() =>
                emit({
                  ...doc,
                  lines: [
                    ...doc.lines,
                    {
                      id: `line_${Date.now()}`,
                      type: "line",
                      words: [{ id: `word_${Date.now()}`, text: "", chord: null }],
                    },
                  ],
                })
              }
              className="mt-4 flex items-center gap-1.5 text-[10px] text-[#B8AEA3] hover:text-[#7B2335] transition-colors px-2 py-1 rounded-lg hover:bg-[#F0EAE0]"
            >
              <Plus className="w-3 h-3" />
              Nueva línea
            </button>
          </div>
        )}
      </div>

      {/* ─── Chord Picker Flotante ─── */}
      <AnimatePresence>
        {chordPickerOpen && mode === "chords" && (
          <ChordPickerPopover
            position={pickerPosition}
            usedChords={usedChords}
            customChord={customChord}
            onCustomChange={setCustomChord}
            onApply={applyChord}
            onRemove={removeChordFromSelection}
            onClose={() => setChordPickerOpen(false)}
          />
        )}
      </AnimatePresence>

      {pasteResult && (
        <SmartPasteModal
          result={pasteResult}
          onUseProcessed={handleUseProcessed}
          onUseOriginal={handleUseOriginal}
          onCancel={() => setPasteResult(null)}
        />
      )}
    </div>
  );
};

// ─── Subcomponente: Fila de línea ───
const CanvasLineRow: React.FC<{
  line: CanvasLine;
  lineIndex: number;
  isActive: boolean;
  selectedIds: Set<string>;
  onWordClick: (id: string, e: React.MouseEvent) => void;
  onWordDoubleClick: (id: string, e: React.MouseEvent) => void;
  onLineClick: () => void;
  onAddLine: () => void;
  onAddSection: (name: string) => void;
}> = ({
  line,
  selectedIds,
  onWordClick,
  onWordDoubleClick,
  onLineClick,
  onAddLine,
  onAddSection,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  if (line.type === "empty") {
    return <div className="h-3" />;
  }

  if (line.type === "section") {
    return (
      <div className="flex items-center gap-2 my-4 group">
        <span className="flex-1 h-px bg-[#E8E0D4]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#7B2335]">
          {line.name}
        </span>
        <span className="flex-1 h-px bg-[#E8E0D4]" />
      </div>
    );
  }

  return (
    <div
      className="group relative py-1 -mx-1 px-1 rounded-lg hover:bg-[#F5F0E8]/50 transition-colors"
      onClick={onLineClick}
    >
      {/* Hover actions */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full pr-1 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5">
        <button
          onClick={(e) => { e.stopPropagation(); onAddLine(); }}
          className="p-1 rounded hover:bg-[#F0EAE0] text-[#B8AEA3] hover:text-[#7B2335]"
          title="Agregar línea"
        >
          <Plus className="w-3 h-3" />
        </button>
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 rounded hover:bg-[#F0EAE0] text-[#B8AEA3] hover:text-[#7B2335]"
            title="Agregar sección"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="absolute left-full top-0 ml-1 bg-white border border-[#E8E0D4] rounded-lg shadow-lg p-1 z-20 min-w-[100px]"
              >
                {["Intro", "Verso", "Pre-coro", "Coro", "Puente", "Solo", "Outro"].map((name) => (
                  <button
                    key={name}
                    onClick={(e) => { e.stopPropagation(); onAddSection(name); setShowMenu(false); }}
                    className="block w-full text-left px-2 py-1 text-[10px] text-[#5A524A] hover:bg-[#F5F0E8] rounded transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Palabras */}
      <div className="flex flex-wrap items-baseline gap-x-0.5 gap-y-3 leading-relaxed">
        {line.words.map((word) => (
          <WordChip
            key={word.id}
            word={word}
            isSelected={selectedIds.has(word.id)}
            onClick={(e) => onWordClick(word.id, e)}
            onDoubleClick={(e) => onWordDoubleClick(word.id, e)}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Subcomponente: Palabra interactiva ───
const WordChip: React.FC<{
  word: CanvasWord;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}> = ({ word, isSelected, onClick, onDoubleClick }) => {
  return (
    <span
      className={`relative inline-block cursor-pointer rounded px-0.5 py-0.5 transition-all ${
        isSelected
          ? "bg-[#7B2335]/15 text-[#7B2335] shadow-sm ring-1 ring-[#7B2335]/30"
          : "hover:bg-[#F0EAE0] text-[#2C2825]"
      }`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={word.chord ? `Acorde: ${word.chord}` : "Clic para seleccionar, doble clic para acorde"}
    >
      {/* Acorde flotante arriba */}
      {word.chord && (
        <span className="absolute -top-4 left-0 text-[10px] font-bold text-[#7B2335] whitespace-nowrap select-none pointer-events-none">
          {word.chord}
        </span>
      )}
      <span className="text-base font-serif leading-relaxed">
        {word.text}
      </span>
    </span>
  );
};

// ─── Subcomponente: Popover de acordes ───
const ChordPickerPopover: React.FC<{
  position: { x: number; y: number };
  usedChords: string[];
  customChord: string;
  onCustomChange: (v: string) => void;
  onApply: (chord: string) => void;
  onRemove: () => void;
  onClose: () => void;
}> = ({ position, usedChords, customChord, onCustomChange, onApply, onRemove, onClose }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<"root" | "quality">("root");
  const [selectedRoot, setSelectedRoot] = useState<string>("");

  // Ajustar posición para no salirse de la pantalla
  const [adjustedPos, setAdjustedPos] = useState(position);
  useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let x = position.x - rect.width / 2;
    let y = position.y - rect.height - 8;
    if (x < 8) x = 8;
    if (x + rect.width > window.innerWidth - 8) x = window.innerWidth - rect.width - 8;
    if (y < 8) y = position.y + 24;
    setAdjustedPos({ x, y });
  }, [position, step]);

  const handleSelectRoot = (root: string) => {
    setSelectedRoot(root);
    setStep("quality");
  };

  const handleSelectQuality = (quality: string) => {
    onApply(selectedRoot + quality);
    setStep("root");
    setSelectedRoot("");
  };

  const previewChord = selectedRoot + (customChord.replace(selectedRoot, "") || "");

  return (
    <motion.div
      ref={popoverRef}
      initial={{ opacity: 0, scale: 0.9, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 8 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className="fixed z-50 bg-white rounded-xl border border-[#E8E0D4] shadow-xl p-3 w-64"
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#8A8078]">
          {step === "root" ? "Selecciona la raíz" : `${selectedRoot} — Selecciona tipo`}
        </span>
        <div className="flex items-center gap-1">
          {step === "quality" && (
            <button
              onClick={() => setStep("root")}
              className="text-[9px] text-[#7B2335] hover:underline"
            >
              ← Volver
            </button>
          )}
          <button onClick={onClose} className="text-[#B8AEA3] hover:text-[#5A524A]">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Acordes usados recientemente */}
      {usedChords.length > 0 && step === "root" && (
        <div className="mb-2 flex flex-wrap gap-1">
          {usedChords.slice(0, 8).map((c) => (
            <button
              key={c}
              onClick={() => onApply(c)}
              className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-[#F5F0E8] text-[#7B2335] hover:bg-[#7B2335] hover:text-white transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {step === "root" ? (
        <>
          {/* Grid de raíces */}
          <div className="grid grid-cols-6 gap-0.5 mb-2">
            {ROOT_NOTES.map((root) => (
              <button
                key={root}
                onClick={() => handleSelectRoot(root)}
                className="py-1 text-[10px] font-bold rounded hover:bg-[#7B2335] hover:text-white transition-colors text-[#5A524A] bg-[#FAF8F5] border border-[#E8E0D4]"
              >
                {root}
              </button>
            ))}
          </div>

          {/* Custom chord input */}
          <div className="flex gap-1.5">
            <input
              type="text"
              value={customChord}
              onChange={(e) => onCustomChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && customChord.trim()) onApply(customChord.trim()); }}
              placeholder="Cmaj7..."
              className="flex-1 px-2 py-1 text-xs rounded-lg border border-[#E8E0D4] bg-[#FAF8F5] focus:outline-none focus:border-[#7B2335] focus:ring-1 focus:ring-[#7B2335]/20"
              autoFocus
            />
            <button
              onClick={() => customChord.trim() && onApply(customChord.trim())}
              className="px-2 py-1 text-xs font-semibold bg-[#7B2335] text-white rounded-lg hover:bg-[#5A1A28] transition-colors"
            >
              OK
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Grid de calidades */}
          <div className="grid grid-cols-3 gap-1 mb-2">
            {QUALITIES.map((q) => (
              <button
                key={q.value}
                onClick={() => handleSelectQuality(q.value)}
                className="py-1.5 text-[10px] font-bold rounded hover:bg-[#7B2335] hover:text-white transition-colors text-[#5A524A] bg-[#FAF8F5] border border-[#E8E0D4]"
              >
                {selectedRoot}{q.label}
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={onRemove}
        className="mt-2 w-full text-center text-[10px] text-[#B8AEA3] hover:text-red-600 transition-colors py-1"
      >
        Eliminar acorde
      </button>
    </motion.div>
  );
};
